// components/entities/LeadRenderer.tsx
import React, { useCallback } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { LeasingLeadDTO, BaseEntityProps, EntityRowAction, PortfolioFitDTO } from '#types/types';
import { Loader2, Target } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { useKernelStore } from '@/kernel/kernelStores';
import { newlyAddedLeadIds } from '#lib/leasing/newlyAddedLeads';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import styles from '../portal/Table.module.css';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';
import type { TableColumnDef } from '../portal/Table';
import { TableSelectionCheckbox } from '../portal/TableSelectionCheckbox';
import { notionStatusPillStyle } from '@/components/ui/notionColors';
import { renderRubricSignal as renderSharedRubricSignal, renderCriterionBar, renderFitPill, renderRunningSignal, useShortlistRunningSignal, resolveCellStyle, formatRelativeTimestamp } from './shortlistCells';
import { AnimatedScore, useScoreSettleRef } from './scoreMotion';
import { useKernel } from '@/kernel/Kernel';
import { ProspectionContactedCell, ProspectionChannelCell, ProspectionResponseCell, ProspectionCommentCell } from './prospectionCells';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { activeCampaignResearch, campaignSelection, researchFieldsLabel, researchingFieldsOf } from '#lib/leasing/researchPass';

const translations = {
  en: {
    statusFailed: 'Failed',
    statusPending: 'Pending',
    statusRunning: 'Running',
    statusAborted: 'Aborted',
    statusReverted: 'Reverted',
    statusInteresting: 'Interesting',
    statusDismissed: 'Dismissed',
    analysisInterrupted: 'Analysis interrupted (Mission stopped).',
    waitingInQueue: 'Waiting in queue (not picked yet).',
    initializing: 'Initializing research...',
    statusDone: 'Done',
  },
  fr: {
    statusDone: 'Terminé',
    statusFailed: 'Échec',
    statusPending: 'En attente',
    statusRunning: 'En cours',
    statusAborted: 'Interrompu',
    statusReverted: 'Restauré',
    statusInteresting: 'Intéressant',
    statusDismissed: 'Écarté',
    waitingInQueue: 'En attente (pas encore pris par le worker).',
    analysisInterrupted: 'Analyse interrompue (Mission arrêtée).',
    initializing: 'Initialisation de la recherche...',
  },
};

const DEFAULT_ROW_COLUMNS: TableColumnDef[] = ['property', 'brand', 'leadSignal', 'leadFit', 'portfolioSignal', 'portfolioFit', 'status', 'actions'].map(
  (id) => ({ id, header: '', minWidth: 1 })
);

export interface LeadRendererProps extends BaseEntityProps<LeasingLeadDTO> {
  lockedUnitId?: string;
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingLeadDTO>[];
}

export const LeadRenderer = React.memo(({
  entityId,
  variant = 'row',
  onClick,
  className,
  columns,
  rowActions = [],
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
  onSelectionZoneMouseDown,
}: LeadRendererProps) => {
  const glass = useTableGlass();
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const data = useKernelStore(state => state.inventory.Lead.registry[entityId]) as LeasingLeadDTO | undefined;
  const updateProspection = useCallback(
    (patch: Partial<LeasingLeadDTO>) => {
      if (entityId) void kernel.current?.updateEntity('Lead', entityId, patch);
    },
    [kernel, entityId]
  );
  const handleProspectionContacted = useCallback(
    (next: boolean) => updateProspection({ prospectionContacted: next }),
    [updateProspection]
  );
  const handleProspectionChannel = useCallback(
    (next: string | null) => updateProspection({ prospectionChannel: next }),
    [updateProspection]
  );
  const handleProspectionResponse = useCallback(
    (next: string | null) => updateProspection({ prospectionResponse: next }),
    [updateProspection]
  );
  const handleProspectionComment = useCallback(
    (next: string | null) => updateProspection({ prospectionComment: next }),
    [updateProspection]
  );
  const handleSelectionClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
  const handleSelectionMouseDown = useCallback(
    (event: React.MouseEvent) => onSelectionZoneMouseDown?.(event, entityId),
    [onSelectionZoneMouseDown, entityId]
  );
  const handleToggleSelect = useCallback(() => onToggleSelect?.(entityId), [onToggleSelect, entityId]);
  // IS THIS ROW ARRIVING RIGHT NOW? Answered per row rather than threaded down from the table,
  // because EntityView renders these through a generic prop surface and adding a bespoke channel
  // for one badge is more machinery than the badge is worth. Returned as a boolean, never an
  // object: a selector handing back a fresh value on every store tick is what put an infinite
  // render loop in the composer earlier today.
  const isNewlyAdded = useKernelStore((state) => {
    const campaignId = typeof data?.lastCampaignId === 'string' ? data.lastCampaignId : '';
    if (!campaignId || !entityId) return false;
    const siblings = Object.values(state.inventory.Lead.registry)
      .filter((row): row is LeasingLeadDTO => (row as LeasingLeadDTO | undefined)?.lastCampaignId === campaignId);
    return newlyAddedLeadIds(siblings).has(entityId);
  });

  const brandFromStore = useKernelStore(state => data?.brandId ? state.inventory.Brand.registry[data.brandId] : undefined);
  const propertyFromStore = useKernelStore(state => data?.propertyId ? state.inventory.Property.registry[data.propertyId] : undefined);
  const portfolioFitFromStore = useKernelStore(state =>
    data?.portfolioFitId ? state.inventory.PortfolioFit.registry[data.portfolioFitId] : undefined
  ) as PortfolioFitDTO | undefined;
  const parentCampaignStatus = useKernelStore(state => {
    if (!data?.lastCampaignId) return null;
    const campaign = readCampaignRow(state.inventory, data.lastCampaignId);
    return typeof campaign?.status === 'string' ? campaign.status.toUpperCase() : null;
  });
  // What the run's research stage is fetching for this brand right now, or null (a string, so the
  // selector result is stable across unrelated registry updates).
  const researchingLabel = useKernelStore(state => {
    if (!data?.lastCampaignId || !data?.brandId) return null;
    const research = activeCampaignResearch(readCampaignRow(state.inventory, data.lastCampaignId));
    const fields = researchingFieldsOf(undefined, research, data.brandId);
    return fields?.length ? researchFieldsLabel(fields, lang) : null;
  });
  // Why the run picked this brand, shown on the row until the Judge's verdict replaces it.
  const pickReason = useKernelStore(state => {
    if (!data?.lastCampaignId || !data?.brandId) return null;
    return campaignSelection(readCampaignRow(state.inventory, data.lastCampaignId))?.reasons[data.brandId] ?? null;
  });

  const evaluatedAgoLabel =
    data?.status === 'SUCCESS' ||
    data?.status === 'REVERTED' ||
    data?.status === 'INTERESTING' ||
    data?.status === 'DISMISSED' ||
    data?.status === 'INVALIDATED'
    ? formatRelativeTimestamp(data.updatedAt || data.createdAt, lang)
    : null;

  // What the row says while it works, shared with the two other shortlist renderers.
  const activeRunningSignal = useShortlistRunningSignal({
    entityId,
    status: data?.status,
    updatedAt: data?.updatedAt,
    strategy: data?.strategy,
    rawFacts: data?.rawFacts,
  });

  // A re-score lands row by row over the socket, so each row announces its OWN arrival: a short
  // directional tint the moment its new fitScore is applied. Progressive settling, not one
  // atomic swap. The ref writes the marker attribute straight to the node, so a burst of
  // updates never turns into a burst of React commits.
  const scoreSettleRef = useScoreSettleRef(data?.fitScore);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  const normalizedStatus = (data.status || '').toUpperCase();
  const shouldForceAborted =
    (normalizedStatus === 'RUNNING' || normalizedStatus === 'PENDING') &&
    (parentCampaignStatus === 'ABORTED' || parentCampaignStatus === 'FAILED');
  const effectiveStatus = shouldForceAborted ? 'ABORTED' : normalizedStatus;
  const isPending = effectiveStatus === 'PENDING';
  const isAborted = effectiveStatus === 'ABORTED';
  const isReverted = effectiveStatus === 'REVERTED';
  const isInteresting = effectiveStatus === 'INTERESTING';
  const isDismissed = effectiveStatus === 'DISMISSED' || effectiveStatus === 'INVALIDATED';
  const isSuccess =
    effectiveStatus === 'SUCCESS' ||
    effectiveStatus === 'COMPLETE' ||
    isReverted ||
    isInteresting ||
    isDismissed;
  const isFailed = effectiveStatus === 'FAILED';
  const isRunning = effectiveStatus === 'RUNNING';
  // Only INVALIDATED is a human striking a row out. DISMISSED is now the Judge's own
  // "not a fit" verdict, written on every scored rejection, so styling it as a manual
  // invalidation struck through most of a completed run.
  const isManuallyInvalidated = effectiveStatus === 'INVALIDATED';
  
  // Fall back to the PREVIEW fields the list query already sends on the lead itself. They exist
  // for exactly this ("populated by the list query so the row can show the brand without a
  // separate fetch", types/types.ts) and the row was ignoring them, so every brand read
  // "Unknown Brand" until the full Brand record happened to be hydrated, which, on a 50-row
  // shortlist, is most of them, most of the time.
  const brandName = brandFromStore?.name || data.brandName || 'Unknown Brand';
  const brandLogoUrl = brandFromStore?.logoUrl || data.brandLogoUrl || null;
  const propertyName = propertyFromStore?.name || 'Unknown property';

  const leadFitScoreValue =
    typeof data.fitScore === 'number' && Number.isFinite(data.fitScore)
      ? data.fitScore
      : null;
  const leadFitScoreRatio = leadFitScoreValue !== null ? Math.max(0, Math.min(1, leadFitScoreValue / 100)) : null;

  const portfolioFitScoreValue =
    typeof portfolioFitFromStore?.score === 'number' && Number.isFinite(portfolioFitFromStore.score)
      ? portfolioFitFromStore.score
      : null;
  const portfolioFitScoreRatio = portfolioFitScoreValue !== null ? Math.max(0, Math.min(1, portfolioFitScoreValue / 100)) : null;

  const resolveScoreClasses = (ratio: number | null, shouldColorize: boolean) => {
    let textClass = 'text-white/30';
    let borderClass = 'border-white/5';
    let bgClass = 'bg-white/[0.04]';
    if (shouldColorize && ratio !== null && !isManuallyInvalidated) {
      if (ratio <= 0.4) {
        textClass = 'text-[#FF7369]';
        borderClass = 'border-[#FF7369]/20';
        bgClass = 'bg-[#FF7369]/10';
      } else if (ratio >= 0.8) {
        textClass = 'text-[#4DAB9A]';
        borderClass = 'border-[#4DAB9A]/20';
        bgClass = 'bg-[#4DAB9A]/10';
      } else {
        textClass = 'text-[#FFA344]';
        borderClass = 'border-[#FFA344]/20';
        bgClass = 'bg-[#FFA344]/10';
      }
    }
    if (shouldColorize && ratio !== null && isManuallyInvalidated) {
      textClass = 'text-white/35';
      borderClass = 'border-white/10';
      bgClass = 'bg-white/[0.03]';
    }
    return { textClass, borderClass, bgClass };
  };

  const leadScoreClasses = resolveScoreClasses(leadFitScoreRatio, isSuccess);
  const portfolioScoreClasses = resolveScoreClasses(portfolioFitScoreRatio, portfolioFitScoreRatio !== null);

  const signalSummary =
    !isRunning && !isPending && !isAborted && !isReverted
      ? (typeof data.rationale === 'string' && data.rationale.trim().length > 0 ? data.rationale.trim() : null)
      : pickReason;
  switch (variant) {
    case 'chip':
      return (
        <div className={clsx("flex items-center gap-2 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-white/80 cursor-pointer hover:bg-white/10", className)} onClick={onClick}>
          <Target size={12} />
          <span className="truncate">{brandName}</span>
          {isSuccess && (
            <AnimatedScore
              value={leadFitScoreValue}
              className={clsx("font-mono font-bold ml-1 transition-colors", leadScoreClasses.textClass)}
            />
          )}
          {portfolioFitScoreValue !== null && (
            <AnimatedScore
              value={portfolioFitScoreValue}
              className={clsx("font-mono font-bold ml-1 transition-colors", portfolioScoreClasses.textClass)}
            />
          )}
        </div>
      );
    case 'card':
      return (
        <div
          className={clsx(
            'flex aspect-square w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:border-white/20 hover:bg-white/10',
            className
          )}
          onClick={onClick}
        >
          <div className="flex min-h-0 flex-1 items-center justify-center border-b border-white/10 bg-[#181818] p-2">
            {brandLogoUrl ? (
              <BrandMark logoUrl={brandLogoUrl} name={brandName} size={88} radius={12} />
            ) : (
              <span className="text-[24px] font-semibold tracking-wide text-white/55">
                {brandName.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex h-8 min-w-0 items-center px-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-medium text-white/92">{brandName}</div>
            </div>
          </div>
        </div>
      );
    case 'page':
      return null;
    case 'row': {
      const cellRenderers: Record<string, React.ReactNode> = {
        _selection: isSelectable ? (
          <div
            className="flex h-full items-center justify-center"
            onClick={handleSelectionClick}
            onMouseDown={handleSelectionMouseDown}
          >
            <TableSelectionCheckbox
              checked={isSelected}
              onChange={handleToggleSelect}
              className="opacity-100"
            />
          </div>
        ) : null,
        property: (
          <div className="min-w-0 h-full flex items-center">
            <span className={clsx('truncate text-sm', isManuallyInvalidated ? 'text-white/45 line-through' : 'text-white/80')}>
              {propertyName}
            </span>
          </div>
        ),
        brand: (
          <div className="min-w-0 h-full flex items-center gap-2">
            <BrandMark logoUrl={brandLogoUrl} name={brandName} size={24} radius={5} />
            <div className="min-w-0 flex-1 flex items-center gap-1.5">
              <span className={clsx('truncate text-sm font-medium', isManuallyInvalidated ? 'text-white/50 line-through' : 'text-white')}>
                {brandName}
              </span>
              {/* JUST ADDED. A dot, not a word: the row already carries a brand, a signal, five
                  criteria and a score, and a labelled pill competed with all of it for the same
                  glance. Blue because every other colour here means a verdict - green a pick, grey a
                  dismissal - and "new" is not a verdict. The title is what says it out loud, for
                  anyone who wonders what the dot is. */}
              {isNewlyAdded && (
                <span
                  className="size-1.5 shrink-0 rounded-full bg-[#3B82F6]"
                  title={lang === 'fr' ? 'Ajoutée par la dernière extension' : 'Added by the latest extension'}
                />
              )}
            </div>
          </div>
        ),
        signal: (
          <div className="min-w-0 h-full flex items-center">
            {isSuccess && data.criteriaResults && data.criteriaResults.length > 0 ? (
              renderSharedRubricSignal(data.criteriaResults, { isManuallyInvalidated })
            ) : isRunning ? (
              <div className="h-4 flex items-center w-full min-w-0">
                {renderRunningSignal(activeRunningSignal, t.initializing)}
              </div>
            ) : researchingLabel ? (
              <div className="h-4 flex items-center w-full min-w-0">
                {renderRunningSignal({ kind: 'RESEARCHING', text: researchingLabel }, t.initializing)}
              </div>
            ) : signalSummary ? (
              <span className={clsx('truncate text-[11px]', isSuccess ? 'text-white/55' : 'text-white/45')}>{signalSummary}</span>
            ) : (
              <span className="text-[11px] text-white/25">-</span>
            )}
          </div>
        ),
        leadFit: (
          <div className="h-full flex items-center justify-start">
            {isSuccess ? (
              <div className={clsx("h-7 w-10 flex items-center justify-center rounded border font-mono text-xs font-medium", leadScoreClasses.borderClass, leadScoreClasses.bgClass)}>
                <AnimatedScore
                  value={leadFitScoreValue}
                  className={clsx("transition-colors", leadScoreClasses.textClass)}
                />
              </div>
            ) : isRunning ? (
              <Loader2 size={12} className="animate-spin text-white/45" />
            ) : (
              <span />
            )}
          </div>
        ),
        fit: (
          <div className="h-full flex items-center justify-start">
            {isSuccess ? (
              renderFitPill(data.fitScore, { isManuallyInvalidated })
            ) : isRunning ? (
              <Loader2 size={12} className="animate-spin text-white/45" />
            ) : (
              <span />
            )}
          </div>
        ),
        status: (
          <div className="h-full min-w-0 flex items-center">
            {/* A dot and a word, as ChatGPT marks a state (owner, 2026-09-28). */}
            <span className="inline-flex w-fit items-center gap-2 text-[#CDCDCD]" style={{ fontSize: 13 }}>
              <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: notionStatusPillStyle(effectiveStatus).color }} />
              {isRunning
                ? t.statusRunning
                : isPending
                  ? t.statusPending
                : isAborted
                    ? t.statusAborted
                    : isReverted
                      ? t.statusReverted
                    : effectiveStatus === 'INTERESTING'
                      ? t.statusInteresting
                    : isDismissed
                      ? t.statusDismissed
                    : isFailed
                      ? t.statusFailed
                      : t.statusDone}
            </span>
          </div>
        ),
        evaluated: (
          <div className="h-full min-w-0 flex items-center">
            {evaluatedAgoLabel ? (
              <span className="truncate text-[#8E8E8E]" style={{ fontSize: 13 }}>{evaluatedAgoLabel}</span>
            ) : (
              <span className="text-[#5A5A5A]" style={{ fontSize: 13 }}>-</span>
            )}
          </div>
        ),
        prospectionContacted: (
          <ProspectionContactedCell value={data.prospectionContacted} lang={lang} onChange={handleProspectionContacted} />
        ),
        prospectionChannel: (
          <ProspectionChannelCell value={data.prospectionChannel} lang={lang} onChange={handleProspectionChannel} />
        ),
        prospectionResponse: (
          <ProspectionResponseCell value={data.prospectionResponse} lang={lang} onChange={handleProspectionResponse} />
        ),
        prospectionComment: (
          <ProspectionCommentCell value={data.prospectionComment} lang={lang} onChange={handleProspectionComment} />
        ),
        actions: (
          <div className="h-full flex items-center justify-end">
            <EntityRowActions entityType="Lead" entity={data} rowActions={rowActions} />
          </div>
        ),
      };

      const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMNS;

      return (
        <div
          ref={scoreSettleRef}
          className={clsx(
            styles.gridRow,
            styles.bodyRow,
            'group',
            isSelected ? '!bg-[#3862C21F] hover:!bg-[#3862C22E]' : undefined,
            isManuallyInvalidated && 'opacity-60',
            glass && !isSelected && !isInteresting && !isManuallyInvalidated && TABLE_GLASS_ROW,
            className || (
              isManuallyInvalidated
                ? 'bg-[#151515] hover:bg-[#1A1A1A]'
                : isInteresting
                  ? '!bg-emerald-500/[0.10] hover:bg-[#1E1E1E]'
                  : 'bg-[#181818] hover:bg-[#1E1E1E]'
            )
          )}
          onClick={onClick}
        >
          {activeColumns.map((column) => (
            <div
              key={column.id}
              className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={resolveCellStyle(column)}
            >
              {cellRenderers[column.id]
                ?? (column.id.startsWith('criterion:')
                  ? renderCriterionBar(data.criteriaResults?.find((c) => `criterion:${c.criterionId}` === column.id), isManuallyInvalidated)
                  : null)}
            </div>
          ))}
        </div>
      );
    }
  }
});

LeadRenderer.displayName = 'LeadRenderer';
